import { DecimalPipe } from '@angular/common';
import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';

/**
 * Formats a rate value as a percent string with tier-aware precision.
 *
 * ## ⚠ Input is basis points, not a decimal
 *
 * `100` means `1%`, not `100%`. Pass `42` for `0.420%`, not `0.0042`. The
 * lender backend ships `lendingRate`, `benchmarkFee`, `vwaf`, `vwar` in
 * this form. Recommended convention: name pipe inputs with a `Bps` suffix
 * (`lendingRateBps`, `benchmarkFeeBps`) so the unit is visible at the call
 * site.
 *
 * ## Precision tiers
 *
 * | Magnitude (abs) | Decimals | Example            |
 * |-----------------|----------|--------------------|
 * | `<1%`           | 3        | `0.420%`           |
 * | `≥1%`           | 2        | `1.25%` / `45.00%` |
 *
 * Sub-1% rates keep 3 decimals so small fee differences stay visible; every
 * rate at or above 1% shows 2 decimals (WI 64383 — one lending-rate format
 * across the lender dashboard, with no 1-decimal drop at ≥10%).
 *
 * ## Special values
 *
 * - `null` / `undefined` / `NaN` / `±Infinity` → `—` (em dash, U+2014).
 * - Negative rates use U+2212 minus (`−0.420%`), not ASCII hyphen.
 *
 * ## Usage
 *
 * Template:
 * ```html
 * {{ instrument.lendingRateBps | sgRatePercent }}
 * ```
 *
 * Static (for ECharts formatters and other non-template callers):
 * ```ts
 * SgRatePercentPipe.transform(42);    // '0.420%'
 * SgRatePercentPipe.transform(-25);   // '−0.250%'
 * SgRatePercentPipe.transform(null);  // '—'
 * ```
 */
@Pipe({
  name: 'sgRatePercent',
  standalone: false,
})
export class SgRatePercentPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private readonly locale: string) {}

  transform(valueBps: number | null | undefined): string {
    return SgRatePercentPipe.transform(valueBps, this.locale);
  }

  /**
   * Static variant — same behavior, no DI required.
   *
   * @param valueBps  Rate in basis points (integer).
   * @param locale    BCP-47 locale code. Defaults to `'en-US'`.
   *
   * @example
   * SgRatePercentPipe.transform(42);       // '0.420%'
   * SgRatePercentPipe.transform(125);      // '1.25%'
   * SgRatePercentPipe.transform(4500);     // '45.00%'
   * SgRatePercentPipe.transform(-42);      // '−0.420%'
   * SgRatePercentPipe.transform(null);     // '—'
   */
  static transform(valueBps: number | null | undefined, locale: string = 'en-US'): string {
    if (valueBps == null || !Number.isFinite(valueBps)) {
      return '—';
    }

    const percent = valueBps / 100;
    const absPercent = Math.abs(percent);
    const digitsInfo = absPercent < 1 ? '1.3-3' : '1.2-2';
    const formatted = new DecimalPipe(locale).transform(percent, digitsInfo, locale);

    return `${formatted}%`.replace(/-/g, '−');
  }
}
